import { trackOutputAction } from 'analytics';
import forge from 'apis/forge';
import { putTextOnClipboard } from 'clipboard';
import Icon, { IconType } from 'components/Icon';
import { Scene } from 'dragonmouse-shared/interfaces';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';
import { unthemed } from 'tokens';
import { sceneShareUrl } from 'utils';
import Modal from '../..';
import * as ModalStyles from '../../styles';
import SceneUserManagerModal from '../SceneUserManagerModal';
import * as S from '../styles';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import { shallowEqual } from 'react-redux';
import { useSelector } from 'react-redux';

interface ShareSceneModalProps {
	scene: Scene;
	onClose: () => void;
}

const ShareSceneModal = ({ scene, onClose }: ShareSceneModalProps) => {
	const theme = useTheme();
	const [showManageEditors, setShowManageEditors] = useState(false);
	const [publiclyReadable, setPubliclyReadable] = useState(!!scene?.metadata?.publiclyReadable);

	const { isMobile } = useSelector((r: RootReducer) => {
		return {
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	const canNativeShare =
		isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const handleAccessChange = async (newPubliclyReadable: boolean) => {
		try {
			await forge.updateScene(
				{
					update: {
						id: scene.id,
						metadata: {
							...scene.metadata,
							publiclyReadable: newPubliclyReadable,
						},
					},
				},
				true
			);
			toast.success('Project access updated');
			setPubliclyReadable(newPubliclyReadable);
		} catch (error) {
			toast.error('Failed to update project access settings');
			console.error('Error updating scene access:', error);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderAccessControl = () => {
		return (
			<S.AccessSection>
				<ModalStyles.SectionTitle>Access Control</ModalStyles.SectionTitle>
				<S.ToggleContainer>
					{!scene.viewOnly && (
						<S.ToggleButton selected={!publiclyReadable} onClick={() => handleAccessChange(false)}>
							<S.ToggleButtonContent>
								<S.ToggleButtonIcon>
									<Icon type={IconType.LOCK} color={theme.gray['1000']} width={16} height={16} />
								</S.ToggleButtonIcon>
								<S.ToggleButtonText>Only project editors</S.ToggleButtonText>
							</S.ToggleButtonContent>
						</S.ToggleButton>
					)}
					<S.ToggleButton
						selected={publiclyReadable || scene.viewOnly}
						onClick={() => handleAccessChange(true)}
					>
						<S.ToggleButtonContent>
							<S.ToggleButtonIcon>
								<Icon type={IconType.GLOBE} color={theme.gray['1000']} width={16} height={16} />
							</S.ToggleButtonIcon>
							<S.ToggleButtonText>Anyone with the link can view</S.ToggleButtonText>
						</S.ToggleButtonContent>
					</S.ToggleButton>
				</S.ToggleContainer>
				{!scene.viewOnly && !isMobile && (
					<S.ManageLink
						onClick={() => {
							setShowManageEditors(true);
						}}
					>
						Manage Project Editors →
					</S.ManageLink>
				)}
			</S.AccessSection>
		);
	};

	if (showManageEditors) {
		return (
			<SceneUserManagerModal
				scene={scene}
				onBack={() => setShowManageEditors(false)}
				onClose={close}
			/>
		);
	}

	return (
		<Modal title={'Share this project'} cancelLabel="Close" submitLabel="Save" onClose={close}>
			<>
				{renderAccessControl()}
				<S.LinkSection>
					<S.LinkButton
						onClick={async () => {
							const url = sceneShareUrl(scene);

							if (canNativeShare) {
								try {
									await navigator.share({
										title: scene.title ?? 'Moonchild project',
										url,
									});
									trackOutputAction('share_scene_link');
									return;
								} catch (error) {
									if ((error as DOMException)?.name === 'AbortError') {
										return;
									}
									console.warn('Native share failed, falling back to copy', error);
								}
							}

							try {
								await putTextOnClipboard(url);
								trackOutputAction('copy_scene_link');
								toast.success('Link copied');
							} catch (error) {
								toast.error('Copy failed — please copy manually');
								console.error('Failed to copy project link to clipboard', error);
							}
						}}
					>
						<Icon
							type={canNativeShare ? IconType.SHARE : IconType.COPY}
							color={unthemed.gray['0']}
							width={14}
							height={14}
						/>
						<S.Link>{canNativeShare ? 'Share project link' : 'Copy project link'}</S.Link>
					</S.LinkButton>
				</S.LinkSection>
			</>
		</Modal>
	);
};

export default ShareSceneModal;
